import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { hitApi, commonToast } from "../../components/commonAction";
import appConfig from "../../config/appConfig";

export default function PlanCheckout({ pageTitle }) {
    const { productCode, planCode } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        hitApi({ url: "billing/products", method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            const p = (resp?.data?.products || []).find(p => p.code === productCode);
            if (!p) { setProduct(null); return; }
            setProduct(p);
            const pl = (p.plans || []).find(pl => pl.code === planCode);
            setPlan(pl || null);
        });
    }, [productCode, planCode]);

    const buy = () => {
        if (!plan) return;
        setSubmitting(true);
        hitApi(
            {
                url: "billing/checkout/plan",
                method: "POST",
                data: {
                    productPlanId: plan._id,
                    affiliateRef: readPxlRefFromStorage(),
                },
                alert: false,
            },
            (resp) => {
                setSubmitting(false);
                if (resp?.status && resp.data?.approvalUrl) {
                    window.location.href = resp.data.approvalUrl;
                } else if (resp?.status) {
                    commonToast("error", "PayPal didn't return an approval URL. Try again.");
                }
            }
        );
    };

    function readPxlRefFromStorage() {
        try {
            const raw = localStorage.getItem('pxl_ref');
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed?.code || !parsed?.expiresAt || parsed.expiresAt < Date.now()) {
                localStorage.removeItem('pxl_ref');
                return null;
            }
            return parsed.code;
        } catch (_) { return null; }
    }

    return (
        <div className="pxl-public-shell">
            <Helmet><title>{pageTitle}</title></Helmet>
            <header className="pxl-public-header">
                <Link to="/pricing">
                    <img src={appConfig.appLogo} alt={appConfig.appName} />
                </Link>
                <Link to="/pricing" className="pxl-public-header-link">All plans</Link>
            </header>

            <div className="pxl-checkout-shell">
                {loading ? (
                    <div style={{ textAlign: "center", color: "#8a8fa6", padding: "48px 0" }}>Loading…</div>
                ) : !plan ? (
                    <div className="pxl-status-banner pxl-status-banner--warning">
                        Plan <code>{planCode}</code> for product <code>{productCode}</code> not found.{" "}
                        <Link to="/pricing">Browse plans</Link>.
                    </div>
                ) : (
                    <div className="pxl-checkout-card">
                        <div className="pxl-checkout-card-title">{product.name}</div>
                        <div className="pxl-checkout-card-subtitle">{plan.name}</div>
                        {plan.description && (
                            <div style={{ color: "#4f536f", fontSize: 14, marginTop: 8 }}>{plan.description}</div>
                        )}

                        <div className="pxl-checkout-card-price">
                            <span className="pxl-checkout-card-price-amount">
                                {plan.currency} {Number(plan.price).toFixed(2)}
                            </span>
                            <span className="pxl-checkout-card-price-period">
                                {plan.billingType === "recurring" ? `/ ${plan.billingInterval}` : "one-time"}
                            </span>
                        </div>

                        {(plan.features || []).length > 0 && (
                            <>
                                <div className="pxl-checkout-list-heading">Features</div>
                                <ul className="pxl-checkout-list">
                                    {plan.features.map((f, i) => <li key={i}>{f}</li>)}
                                </ul>
                            </>
                        )}

                        <div className="pxl-checkout-actions">
                            <button className="pxlCommon-btn" onClick={buy} disabled={submitting}>
                                {submitting ? "Redirecting to PayPal…" : `Pay ${plan.currency} ${Number(plan.price).toFixed(2)}`}
                            </button>
                            <button type="button" className="pxl-checkout-cancel-link" onClick={() => navigate("/pricing")}>
                                Cancel
                            </button>
                        </div>
                        <div className="pxl-checkout-trust">
                            <svg className="pxl-checkout-trust-icon" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                <path d="M8 1a3 3 0 0 0-3 3v3H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-1V4a3 3 0 0 0-3-3Zm0 2a1.5 1.5 0 0 1 1.5 1.5V7h-3V4.5A1.5 1.5 0 0 1 8 3Z"/>
                            </svg>
                            Secure checkout via PayPal — your card details never touch our servers.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
